"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import {
  MessageSquarePlus,
  Bug,
  Lightbulb,
  MessageCircle,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/shared/ui/dialog";
import { Textarea } from "@/components/shared/ui/textarea";
import { Label } from "@/components/shared/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/shared/ui/radio-group";
import { cn } from "@/lib/utils/shared/core-utils";

/**
 * @file FeedbackWidget.tsx
 * @description 피드백 위젯 컴포넌트
 *
 * 주요 기능:
 * 1. 피드백 카테고리 선택 (기능 제안, 버그 신고, 기타 의견)
 * 2. 피드백 내용 입력 및 제출
 * 3. Google Apps Script를 통한 피드백 저장
 * 4. 이벤트 추적 (GA, Meta Pixel)
 *
 * @dependencies
 * - @/lib/utils/analytics: GA 이벤트 추적
 * - @/lib/utils/meta-pixel: Meta Pixel 이벤트 추적
 */

interface FeedbackWidgetProps {
  /** 외부에서 모달 열림 상태를 제어할 수 있는 prop */
  open?: boolean;
  /** 외부에서 모달 열림 상태 변경을 제어할 수 있는 prop */
  onOpenChange?: (open: boolean) => void;
  /** 플로팅 버튼 표시 여부 (기본값: true) */
  showFloatingButton?: boolean;
}

export function FeedbackWidget({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  showFloatingButton = true,
}: FeedbackWidgetProps = {}) {
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [category, setCategory] = useState("suggestion"); // suggestion | bug | other
  const [message, setMessage] = useState("");

  // 외부 제어가 있으면 외부 상태 사용, 없으면 내부 상태 사용
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = controlledOnOpenChange || setInternalOpen;

  // 클라이언트 마운트 후에만 렌더링 (hydration mismatch 방지)
  useEffect(() => {
    setMounted(true);
  }, []);

  // 시뮬레이션 상세 페이지에서는 피드백 버튼 숨김
  const isSimulationDetailPage =
    pathname?.startsWith("/simulations/") && pathname !== "/simulations";

  if (isSimulationDetailPage) {
    return null;
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    // 다이얼로그 상태 관리
    if (!open) {
      // 다이얼로그가 닫힐 때 상태 초기화
      if (isSuccess) {
        setIsSuccess(false);
        setMessage("");
        setCategory("suggestion");
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) {
      return;
    }

    setIsSubmitting(true);
    console.log("[FeedbackWidget] 피드백 제출 시작:", {
      category,
      message: message.substring(0, 50),
    });

    try {
      const response = await fetch("/api/systems/feedback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          category,
          message: message.trim(),
        }),
      });

      console.log(
        "[FeedbackWidget] API 응답 상태:",
        response.status,
        response.statusText,
      );

      // Response body는 한 번만 읽을 수 있으므로, 먼저 text로 읽고 JSON 파싱 시도
      const responseText = await response.text();
      let result;

      // 404 응답인 경우 HTML이 반환될 수 있음
      if (response.status === 404) {
        console.error(
          "[FeedbackWidget] API 엔드포인트를 찾을 수 없습니다 (404)",
        );
        throw new Error(
          "API 서버를 찾을 수 없습니다. 개발 서버를 재시작해주세요.",
        );
      }

      try {
        result = JSON.parse(responseText);
        console.log("[FeedbackWidget] API 응답 데이터:", result);
      } catch {
        console.error(
          "[FeedbackWidget] JSON 파싱 실패:",
          responseText.substring(0, 200),
        );
        // HTML 응답인 경우 (404 페이지 등)
        if (
          responseText.trim().startsWith("<!DOCTYPE") ||
          responseText.trim().startsWith("<html")
        ) {
          throw new Error(
            "서버가 HTML을 반환했습니다. API 엔드포인트를 확인해주세요.",
          );
        }
        throw new Error("서버 응답을 처리할 수 없습니다.");
      }

      if (!response.ok) {
        console.error("[FeedbackWidget] API 오류:", result);
        throw new Error(result?.error || `서버 오류 (${response.status})`);
      }

      console.log("[FeedbackWidget] 피드백 제출 성공:", result);

      setIsSubmitting(false);
      setIsSuccess(true);

      // 2초 후 자동으로 닫기 및 초기화
      setTimeout(() => {
        setIsOpen(false);
        setIsSuccess(false);
        setMessage("");
        setCategory("suggestion");
      }, 2000);
    } catch (error) {
      console.error("[FeedbackWidget] 피드백 제출 실패:", error);
      setIsSubmitting(false);

      alert(
        error instanceof Error
          ? error.message
          : "피드백 전송에 실패했습니다. 잠시 후 다시 시도해주세요.",
      );
    }
  };

  // 클라이언트에서만 렌더링 (hydration mismatch 방지)
  if (!mounted) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {showFloatingButton && (
        <DialogTrigger asChild>
          <Button
            className="fixed bottom-6 right-6 h-12 sm:h-14 rounded-full shadow-xl bg-slate-900 hover:bg-indigo-600 text-white transition-all duration-300 hover:scale-110 z-50 px-6 sm:px-8 gap-2 font-semibold text-sm sm:text-base"
            aria-label="피드백 보내기"
          >
            <MessageSquarePlus className="h-5 w-5 sm:h-6 sm:w-6" />
            <span className="hidden sm:inline">피드백 보내기</span>
            <span className="sm:hidden">피드백</span>
          </Button>
        </DialogTrigger>
      )}

      <DialogContent className="sm:max-w-[425px] bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800">
        {/* 성공 상태 UI */}
        {isSuccess ? (
          <div className="flex flex-col items-center justify-center py-10 text-center animate-in fade-in zoom-in duration-300">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
              소중한 의견 감사합니다!
            </h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              보내주신 내용은 서비스 개선에
              <br />큰 도움이 됩니다.
            </p>
          </div>
        ) : (
          /* 입력 폼 UI */
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <span className="text-indigo-600 dark:text-indigo-400">
                  BIDIX
                </span>
                에게 의견 보내기
              </DialogTitle>
              <DialogDescription>
                서비스 이용 중 불편한 점이나 제안하고 싶은 아이디어가 있다면
                자유롭게 말씀해 주세요.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-6 py-6">
              {/* 카테고리 선택 (Card Style Radio) */}
              <div className="space-y-3">
                <Label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  유형 선택
                </Label>
                <RadioGroup
                  defaultValue="suggestion"
                  value={category}
                  onValueChange={setCategory}
                  className="grid grid-cols-3 gap-3"
                >
                  <div>
                    <RadioGroupItem
                      value="suggestion"
                      id="suggestion"
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor="suggestion"
                      className="flex flex-col items-center justify-between rounded-lg border-2 border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 hover:bg-slate-50 dark:hover:bg-slate-800 peer-data-[state=checked]:border-indigo-600 dark:peer-data-[state=checked]:border-indigo-500 peer-data-[state=checked]:text-indigo-600 dark:peer-data-[state=checked]:text-indigo-400 [&:has([data-state=checked])]:border-indigo-600 dark:[&:has([data-state=checked])]:border-indigo-500 transition-all cursor-pointer text-center h-24"
                    >
                      <Lightbulb className="mb-2 h-5 w-5" />
                      <span className="text-xs font-bold">기능 제안</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem
                      value="bug"
                      id="bug"
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor="bug"
                      className="flex flex-col items-center justify-between rounded-lg border-2 border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 hover:bg-slate-50 dark:hover:bg-slate-800 peer-data-[state=checked]:border-red-500 dark:peer-data-[state=checked]:border-red-500 peer-data-[state=checked]:text-red-600 dark:peer-data-[state=checked]:text-red-400 [&:has([data-state=checked])]:border-red-600 dark:[&:has([data-state=checked])]:border-red-500 transition-all cursor-pointer text-center h-24"
                    >
                      <Bug className="mb-2 h-5 w-5" />
                      <span className="text-xs font-bold">버그 신고</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem
                      value="other"
                      id="other"
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor="other"
                      className="flex flex-col items-center justify-between rounded-lg border-2 border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 hover:bg-slate-50 dark:hover:bg-slate-800 peer-data-[state=checked]:border-slate-900 dark:peer-data-[state=checked]:border-slate-200 peer-data-[state=checked]:text-slate-900 dark:peer-data-[state=checked]:text-slate-100 [&:has([data-state=checked])]:border-slate-900 dark:[&:has([data-state=checked])]:border-slate-200 transition-all cursor-pointer text-center h-24"
                    >
                      <MessageCircle className="mb-2 h-5 w-5" />
                      <span className="text-xs font-bold">기타 의견</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* 내용 입력 */}
              <div className="space-y-3">
                <Label
                  htmlFor="message"
                  className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
                >
                  내용
                </Label>
                <Textarea
                  id="message"
                  placeholder={
                    category === "bug"
                      ? "어떤 상황에서 오류가 발생했나요? 자세히 적어주시면 빠른 해결이 가능합니다."
                      : "자유롭게 의견을 남겨주세요."
                  }
                  className="min-h-[120px] resize-none text-sm focus:ring-indigo-500 dark:bg-slate-900 dark:border-slate-800"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="submit"
                className={cn(
                  "w-full font-bold",
                  category === "bug"
                    ? "bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800"
                    : "bg-slate-900 hover:bg-indigo-600 dark:bg-slate-800 dark:hover:bg-indigo-700",
                )}
                disabled={isSubmitting || !message.trim()}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    전송 중...
                  </>
                ) : (
                  "의견 보내기"
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
